export interface PayoutResult {
  coins: number;
  grossUsd: number;
  ilorisFee: number;
  payoutFee: number;
  netAmount: number;
  isEligible: boolean;
  message?: string;
}

export const EXCHANGE_RATE = 0.50; // $0.50 USD per coin
export type PayoutMethod = "stripe" | "paypal";
export const PAYOUT_POLICIES = {
  stripe: { feePercent: 7, minimumCoins: 20 },
  paypal: { feePercent: 9, minimumCoins: 50 },
} as const satisfies Record<PayoutMethod, { feePercent: number; minimumCoins: number }>;

/**
 * Calculates the exact USD payout a writer will receive given an amount of coins.
 */
export function calculatePayout(coins: number, method: PayoutMethod = "stripe"): PayoutResult {
  const { feePercent, minimumCoins } = PAYOUT_POLICIES[method];
  const minimumUsd = minimumCoins * EXCHANGE_RATE;
  const grossUsd = coins * EXCHANGE_RATE;
  
  if (grossUsd < minimumUsd) {
    return {
      coins,
      grossUsd,
      ilorisFee: 0,
      payoutFee: 0,
      netAmount: 0,
      isEligible: false,
      message: `Minimum cashout is $${minimumUsd.toFixed(2)} (${minimumCoins} coins). You have $${grossUsd.toFixed(2)}.`
    };
  }

  // Round the fee once in cents, then subtract it so the breakdown balances.
  const grossCents = Math.round(grossUsd * 100);
  const feeCents = Math.round(grossCents * feePercent / 100);

  return {
    coins,
    grossUsd: grossCents / 100,
    ilorisFee: feeCents / 100,
    payoutFee: 0,
    netAmount: (grossCents - feeCents) / 100,
    isEligible: true,
  };
}
